import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Email,
  Business,
  Phone,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { commonAPI, DepartmentOption, accountAPI, ApplyAccountPayload } from '../../services/api';
import { LoginFormData, RegisterFormData } from '../../types/auth';

/** 圖片路徑：先嘗試 login_pic_1，再 fallback 到 login_pic */
const loginBrandCandidates = ['login_pic_1.png', 'login_pic.png'];
const loginPageBgColor = '#2f343a';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const imageVersion = useMemo(() => Date.now(), []);
  const [brandImageIndex, setBrandImageIndex] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [loginData, setLoginData] = useState<LoginFormData>({
    username: '',
    password: '',
  });
  const [registerData, setRegisterData] = useState<RegisterFormData>({
    username: '',
    name: '',
    password: '',
    confirmPassword: '',
    email: '',
    department: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [deptLoading, setDeptLoading] = useState(false);

  const loadDepartments = async () => {
    try {
      setDeptLoading(true);
      const data = await commonAPI.getDepartments();
      setDepartments(data);
    } catch (e) {
      // ignore
    } finally {
      setDeptLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError(null);
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError(null);
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(loginData);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || '登入失敗，請檢查用戶名和密碼');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // 基本驗證
    if (registerData.password !== registerData.confirmPassword) {
      setError('密碼確認不一致');
      setLoading(false);
      return;
    }

    if (registerData.password.length < 6) {
      setError('密碼長度至少6位');
      setLoading(false);
      return;
    }

    try {
      // 呼叫後端申請 API
      const applyData: ApplyAccountPayload = {
        username: registerData.username,
        name: registerData.name,
        password: registerData.password,
        email: registerData.email,
        department_id: Number(registerData.department),
        status: 'pending',
        datetime: new Date().toISOString() // 當前時間
      };
      
      await accountAPI.apply(applyData);
      // 成功提示
      window.alert('申請已提交，請等待管理員審核');
      // 申請成功後清除所有欄位
      setRegisterData({
        username: '',
        name: '',
        password: '',
        confirmPassword: '',
        email: '',
        department: ''
      });
      // 申請成功後導向登入頁
      setTimeout(() => {
        setTabValue(0);
      }, 0);
    } catch (err: any) {
      // 處理錯誤訊息
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const errorMessages = err.response.data.errors.join('\n');
        setError(errorMessages);
      } else {
        setError(err.response?.data?.message || '申請失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: loginPageBgColor,
      }}
    >
      {/* 左側：品牌圖 */}
      <Box
        sx={{
          flex: { md: '0 0 48%' },
          minHeight: { xs: 220, md: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-end' },
          bgcolor: loginPageBgColor,
          px: { xs: 2, md: 1.25 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 430,
            p: 1,
            borderRadius: 2,
            position: 'relative',
            background: 'linear-gradient(145deg, #92979d 0%, #6f757c 45%, #565c63 100%)',
            border: '1px solid #454b52',
            boxShadow: '0 14px 30px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 6,
              pointerEvents: 'none',
              backgroundImage:
                'radial-gradient(circle at 12px 12px, #dfe3e8 0 2px, #7f868d 3px 5px, transparent 6px), radial-gradient(circle at calc(100% - 12px) 12px, #dfe3e8 0 2px, #7f868d 3px 5px, transparent 6px), radial-gradient(circle at 12px calc(100% - 12px), #dfe3e8 0 2px, #7f868d 3px 5px, transparent 6px), radial-gradient(circle at calc(100% - 12px) calc(100% - 12px), #dfe3e8 0 2px, #7f868d 3px 5px, transparent 6px)',
            },
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}/${loginBrandCandidates[brandImageIndex]}?v=${imageVersion}`}
            alt="DKL SERVICE"
            onError={() => {
              setBrandImageIndex((prev) => (prev < loginBrandCandidates.length - 1 ? prev + 1 : prev));
            }}
            sx={{
              width: '100%',
              maxHeight: { xs: 210, md: 360 },
              objectFit: 'contain',
              borderRadius: 1.25,
              display: 'block',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
            }}
          />
        </Box>
      </Box>

      {/* 右側：登入表單 */}
      <Box
        sx={{
          flex: { md: '1 1 52%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-start' },
          bgcolor: loginPageBgColor,
          p: { xs: 2, md: 1.25 },
        }}
      >
      <Container maxWidth="sm" sx={{ width: '100%' }}>
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 2.5,
            background: 'linear-gradient(160deg, rgba(245,247,250,0.96) 0%, rgba(228,232,237,0.93) 100%)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(120,128,137,0.38)',
            boxShadow: '0 16px 34px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.7)',
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.6,
              backgroundColor: 'rgba(255,255,255,0.72)',
            },
          }}
        >
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: '#1f65ac',
                textShadow: '0 1px 2px rgba(255,255,255,0.55)',
              }}
            >
              達客利機具租借系統
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {tabValue === 0 ? '請登入您的帳戶' : '申請新帳戶'}
            </Typography>
          </Box>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': { backgroundColor: '#4a89dc', height: 3, borderRadius: 3 },
              '& .MuiTab-root': { fontWeight: 600 },
            }}
          >
            <Tab label="登入" />
            <Tab label="申請帳號" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </Alert>
          )}

          {tabValue === 0 ? (
            <form onSubmit={handleLoginSubmit}>
            <TextField
              fullWidth
              label="用戶名"
              name="username"
              value={loginData.username}
              onChange={handleLoginChange}
              margin="normal"
              required
              autoComplete="username"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              fullWidth
              label="密碼"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={loginData.password}
              onChange={handleLoginChange}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                  boxShadow: '0 6px 12px rgba(25, 118, 210, 0.4)',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                '登入'
              )}
            </Button>
            </form>
          ) : (
            <form onSubmit={handleRegisterSubmit}>
              <TextField
                fullWidth
                label="手機號碼"
                name="username"
                value={registerData.username}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="username"
                autoFocus
                placeholder="0912345678"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="姓名"
                name="name"
                value={registerData.name}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="電子郵件(常用)"
                name="email"
                type="email"
                value={registerData.email}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth margin="normal" required sx={{ flex: 1 }}>
                  <InputLabel id="department-label">團隊</InputLabel>
                  <Select
                    labelId="department-label"
                    label="團隊"
                    name="department"
                    value={registerData.department}
                    onChange={(e) => setRegisterData(prev => ({ ...prev, department: String(e.target.value) }))}
                  >
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton aria-label="重新整理" onClick={loadDepartments} disabled={deptLoading} sx={{ mt: 1 }}>
                  <Refresh />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                label="密碼"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={registerData.password}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label="確認密碼"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={registerData.confirmPassword}
                onChange={handleRegisterChange}
                margin="normal"
                required
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
                  boxShadow: '0 4px 8px rgba(46, 125, 50, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)',
                    boxShadow: '0 6px 12px rgba(46, 125, 50, 0.4)',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  '申請帳號'
                )}
              </Button>
            </form>
          )}

          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              {tabValue === 0 ? '如有問題，請聯繫系統管理員' : '申請後需要管理員審核才能使用'}
            </Typography>
          </Box>
        </Paper>
      </Container>
      </Box>
    </Box>
  );
};

export default LoginPage;

